<section class="ret-page">

  <header class="ret-header">
    <span class="ret-header__icon"><mat-icon>payments</mat-icon></span>
    <h1>Payments</h1>
  </header>

  <div *ngIf="loading" class="state state--loading">
    <mat-spinner diameter="40"></mat-spinner>
    <p>Loading return…</p>
  </div>

  <div *ngIf="error && !loading" class="state state--error">
    <mat-icon>error_outline</mat-icon>
    <p>{{ error }}</p>
    <button mat-flat-button color="primary" (click)="load()"><mat-icon>refresh</mat-icon> Retry</button>
  </div>

  <div *ngIf="!loading && !error && ret" class="ret-grid">

    <!-- LEFT -->
    <div class="ret-main">

      <!-- Meta strip -->
      <div class="meta-card">
        <div class="meta-cell">
          <span class="meta-cell__label">Period</span>
          <span class="meta-cell__value">{{ ret.period }}</span>
        </div>
        <div class="meta-cell">
          <span class="meta-cell__label">Type</span>
          <span class="meta-cell__value">{{ typeLabel }}</span>
        </div>
        <div class="meta-cell">
          <span class="meta-cell__label">Frequency</span>
          <span class="meta-cell__value">{{ ret.frequency }}</span>
        </div>
        <div class="meta-cell">
          <span class="meta-cell__label">Due date</span>
          <span class="meta-cell__value">{{ ret.dueDate | date: 'MMMM d' }}</span>
        </div>
        <div class="meta-cell">
          <span class="meta-cell__label">Status</span>
          <span class="status-pill" [ngClass]="statusClass(ret.status)">
            <span class="status-pill__dot"></span>{{ ret.status }}
          </span>
        </div>
      </div>

      <p class="supplemental-note" *ngIf="ret.shared">
        This return belongs to {{ ret.owningAccountName }}. You can file and pay it, and edit only your property's line.
      </p>
      <p class="supplemental-note" *ngIf="ret.reportedByRole">
        Reported by {{ ret.reportedByRole === 'OWNER' ? 'Owner' : 'Management Co' }} — {{ ret.reportedByAccountName }}
      </p>

      <p class="supplemental-note" *ngIf="isSupplemental">
        Supplemental return: it covers only the properties added after this period's
        original return{{ ret.sourceReturnId ? ' (#' + ret.sourceReturnId + ')' : '' }} was paid.
        The properties on the original return are not repeated here.
      </p>

      <!-- Installment plan (shown when the return is on a payment plan) -->
      <div class="plan-card" *ngIf="plan">
        <div class="plan-card__head">
          <span class="plan-card__title">Installment plan</span>
          <span class="plan-card__stat">Total paid <strong class="plan-card__paid">{{ plan.totalPaid | currency }}</strong></span>
          <span class="plan-card__stat">Total left <strong>{{ plan.totalLeft | currency }}</strong></span>
          <button type="button" class="plan-card__group" (click)="toggleReturnGroup(true)">
            <mat-icon>hub</mat-icon> Return group
          </button>
        </div>

        <div class="table-scroll">
          <table class="plan-table">
            <thead>
              <tr><th>No</th><th>Payment</th><th>Due date</th><th>Status</th><th class="num">Amount</th></tr>
            </thead>
            <tbody>
              <tr *ngFor="let it of plan.installments"
                  [class.is-current]="it.sequenceNo === plan.currentSequenceNo">
                <td>{{ it.sequenceNo }}</td>
                <td class="period">{{ it.period }}</td>
                <td>{{ it.dueDate | date: 'MM/dd/yyyy' }}</td>
                <td>
                  <span class="status-pill" [ngClass]="statusClass(it.status)">
                    <span class="status-pill__dot"></span>{{ it.status }}
                  </span>
                </td>
                <td class="num amount">{{ it.amount | currency }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="plan-card__actions" *ngIf="plan.nextDueAmount">
          <button mat-flat-button color="primary" (click)="payNextInstallment()">
            <mat-icon>payments</mat-icon>
            Pay {{ plan.nextDueLabel }} ({{ plan.nextDueAmount | currency }})
          </button>
        </div>
      </div>

      <!-- Property entry table (regular returns) -->
      <div class="entry-card" *ngIf="!plan" [formGroup]="form">
        <div class="entry-head">
          <span class="entry-head__addr">Address</span>
          <span class="entry-head__num">Gross rental receipts</span>
          <span class="entry-head__num">Exemptions</span>
        </div>

        <div class="entry-rows" formArrayName="rows">
          <div class="entry-row" *ngFor="let row of rows.controls; let i = index" [formGroupName]="i">
            <span class="entry-row__addr">{{ row.get('address')?.value }}</span>
            <mat-form-field class="entry-row__field" appearance="outline" subscriptSizing="dynamic">
              <span matTextPrefix>$&nbsp;</span>
              <input matInput type="number" min="0" formControlName="gross" placeholder="0.00" appMoney>
            </mat-form-field>
            <mat-form-field class="entry-row__field" appearance="outline" subscriptSizing="dynamic">
              <span matTextPrefix>$&nbsp;</span>
              <input matInput type="number" min="0" formControlName="exemptions" placeholder="0.00" appMoney>
            </mat-form-field>
          </div>
        </div>

        <div class="entry-subtotal">
          <span>Subtotal</span>
          <span class="num">{{ grossTotal | currency }}</span>
          <span class="num">{{ exemptionTotal | currency }}</span>
        </div>
      </div>
    </div>

    <!-- RIGHT: summary -->
    <aside class="ret-side">
      <div class="summary-card">
        <div class="summary-row">
          <span>Taxable rental receipts</span>
          <span class="num">{{ taxableTotal | currency }}</span>
        </div>
        <div class="summary-row">
          <span>Total taxes</span>
          <span class="num">{{ totalTaxes | currency }}</span>
        </div>
        <div class="summary-row">
          <span>Late fee</span>
          <span class="num">{{ lateFee | currency }}</span>
        </div>
        <div class="summary-row">
          <span>Late interest</span>
          <span class="num">{{ lateInterest | currency }}</span>
        </div>
        <div class="summary-row">
          <span>Online collection allowance</span>
          <span class="num">{{ onlineCollectionAllowance | currency }}</span>
        </div>
        <div class="summary-row summary-row--total">
          <span>Total amount due</span>
          <span class="num">{{ totalAmountDue | currency }}</span>
        </div>
      </div>

      <div class="side-actions">
        <button mat-stroked-button *ngIf="canPrint" class="mail-btn" type="button" (click)="mailInPayment()">
          <mat-icon>print</mat-icon> Mail-in payment
        </button>
        <!-- Totals refresh on their own a moment after typing stops; this saves immediately. -->
        <button mat-stroked-button type="button" (click)="saveChanges()" [disabled]="form.pristine || saving">
          <mat-icon>save</mat-icon>
          {{ saving ? 'Saving…' : (form.dirty ? 'Save changes' : 'Saved') }}
        </button>
      </div>
    </aside>
  </div>

  <div class="ret-notice" *ngIf="notice && !loading && ret" role="alert">
    <mat-icon>info</mat-icon><span>{{ notice }}</span>
  </div>

  <!-- Bottom action bar -->
  <div class="ret-actions" *ngIf="!loading && ret">
    <button mat-stroked-button color="primary" type="button" (click)="goBack()">
      <mat-icon>arrow_back</mat-icon> Go back
    </button>
    <button mat-stroked-button color="primary" type="button" (click)="submit()">
      <mat-icon>send</mat-icon> Submit return &mdash; {{ totalAmountDue | currency }} due
    </button>
    <button class="add-basket-btn" mat-flat-button color="primary" type="button" (click)="addToBasket()">
      <mat-icon>add_shopping_cart</mat-icon> Add to cart &amp; keep browsing
    </button>
  </div>

  <!-- Return group modal -->
  <div class="rg-overlay" *ngIf="showReturnGroup && plan" (click)="toggleReturnGroup(false)">
    <div class="rg-modal" (click)="$event.stopPropagation()">
      <div class="rg-modal__head">
        <mat-icon>hub</mat-icon> Return group
      </div>
      <div class="table-scroll">
        <table class="rg-table">
          <thead>
            <tr><th>Month | Year</th><th>Frequency</th><th class="num">Amount</th></tr>
          </thead>
          <tbody>
            <tr *ngFor="let g of plan.groupedReturns">
              <td class="period">{{ g.period }}</td>
              <td>{{ g.frequency }}</td>
              <td class="num amount">{{ g.amount | currency }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="rg-modal__actions">
        <button mat-stroked-button color="primary" type="button" (click)="toggleReturnGroup(false)">
          <mat-icon>arrow_back</mat-icon> Go back
        </button>
      </div>
    </div>
  </div>
</section>
